import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from '../components/ConfirmationModal';

const ConfirmContext = createContext(null);

const DEFAULT_STATE = {
  isOpen: false,
  title: '',
  message: '',
  confirmText: undefined,
  cancelText: undefined,
  isDangerous: true
};

export function ConfirmProvider({ children }) {
  const { t } = useTranslation();
  const [state, setState] = useState(DEFAULT_STATE);
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        isOpen: true,
        title: options.title || t('common.confirm') || 'Confirm',
        message: options.message || '',
        confirmText: options.confirmText,
        cancelText: options.cancelText,
        isDangerous: options.isDangerous !== false
      });
    });
  }, [t]);

  const handleConfirm = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
    if (resolverRef.current) {
      resolverRef.current(true);
      resolverRef.current = null;
    }
  }, []);

  const handleCancel = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmationModal
        isOpen={state.isOpen}
        title={state.title}
        message={state.message}
        confirmText={state.confirmText || t('common.confirm') || 'Confirm'}
        cancelText={state.cancelText || t('common.cancel') || 'Cancel'}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        isDangerous={state.isDangerous}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return ctx;
}
